import { Component, OnInit, Input, Output, OnChanges, EventEmitter } from '@angular/core';
import { FileUploader, FileItem, ParsedResponseHeaders, FileUploaderOptions, Headers } from 'ng2-file-upload';
import { CustomSpinnerService } from 'src/app/core/interceptors/custom-spinner.service';
import { ApplicationWizard } from 'src/app/core/models/application-wizard.model';
import { Artifact } from 'src/app/core/models/artifact.model';
import { HttpService } from 'src/app/core/services/http.service';
import { UtilService } from 'src/app/core/services/util.service';
import { ARTIFACT_KEY } from 'src/app/core/constants/artifacts-order.constants';
import { AppWizService } from 'src/app/core/services/app-wiz.service';
import { ARTIFACT_STATUS_LABEL, ARTIFACT_TYPE } from 'src/app/core/enums/artifact.enum';
import { HttpHeaders } from '@angular/common/http';
import { ErrorLogService } from 'src/app/core/services/error-log.service';
import { UserService } from 'src/app/core/services/user.service';
import { USER_ROLES } from 'src/app/core/enums/user.enum';
import { random } from 'lodash';
import * as pdfjsLib from 'pdfjs-dist';
import JSZip from 'jszip';

(pdfjsLib as any).GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.js';

/**
 * Emitted when a file never made it to S3. Callers use this rather than inspecting
 * document status: the failed entry is removed from the summary, and the persisted
 * status vocabulary belongs to the backend, not the UI.
 */
export interface UploadFailure {
  documentId: string,
  fileName: string,
  status: number,
  response: string,
}

export interface UploadSummary {
  id: string,
  status: 'queue' | 'uploading' | 'uploaded' | 'failed',
  preSignedURL?: string,
  name: string,
  mimeType?: string,
  size: number,
  documentType?: string,
  documentSubType?: string,
  approval?: string,
  createDate: Date,
  siteCode?: string,
  systemCode?: string,
  convertedS3Path?: string,
  approach?: string,
  progress?: number,
  error: string,
}
@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss']
})
export class FileUploadComponent implements OnInit, OnChanges {

  ARTIFACT_STATUS_LABEL = ARTIFACT_STATUS_LABEL;
  multiple: boolean = false;
  size: number = 4;
  extension: Array<String> = [];
  isOneDocType = false;
  selectedDocTypes: any = { documentType: { subTypes: [] }, documentSubType: {} };

  @Input() uploadSummary: UploadSummary[] = [];
  @Input() artifact: Artifact = {} as Artifact;
  @Input() type: string | undefined = '';
  @Input() locked: boolean | undefined = false;
  @Input() artifactUpload: boolean | undefined = false;
  @Input() docTypes: any = null;
  @Input() hideValidation: boolean = false;
  @Input('btnName') btnName: string = 'Upload';
  @Input('appConfig') appConfig: any;

  public uploader: FileUploader = new FileUploader({});
  private uploaderOptions: FileUploaderOptions = {};
  ARTIFACT = ARTIFACT_KEY;
  preSignInData: any;
  compId: number = random(0, 1000);

  @Output() emitData: EventEmitter<Boolean> = new EventEmitter();
  /** True while any selected file has failed to reach S3 and has not been re-uploaded. */
  @Output() uploadFailure: EventEmitter<boolean> = new EventEmitter();
  failedUploads: UploadFailure[] = [];
  @Output() uploadData: EventEmitter<any> = new EventEmitter();

  constructor(
    private http: HttpService,
    private spin: CustomSpinnerService,
    private util: UtilService,
    public appWizService: AppWizService,
    public errorLog: ErrorLogService,
    public userService: UserService,
  ) { }

  ngOnInit(): void {
    this.setUploadConfig();
  }

  ngOnChanges() {
    setTimeout(() => this.initDocType());
  }

  initDocType() {
    if (this.docTypes?.length == 1) {
      this.selectedDocTypes.documentType = this.docTypes[0];
    } else {
      this.selectedDocTypes.documentType = { subTypes: [] };
    }
    if (this.selectedDocTypes.documentType.subTypes.length == 1) {
      this.selectedDocTypes.documentSubType = this.selectedDocTypes.documentType.subTypes[0];
      this.isOneDocType = true;
    } else {
      this.selectedDocTypes.documentSubType = {};
    }
  }

  ngAfterViewInit() {
    this.uploader.onWhenAddingFileFailed = this.onWhenAddingFileFailed.bind(this);
    this.uploader.onAfterAddingFile = (item) => { console.log("onAfterAddingFile", { item }) };
    this.uploader.onErrorItem = this.onErrorItem.bind(this);
    this.uploader.onCancelItem = this.onCancelItem.bind(this);
    this.uploader.onSuccessItem = this.onSuccessItem.bind(this);
    this.uploader.onCompleteAll = () => { console.log("onCompleteAll") };
    this.uploader.onProgressItem = this.onProgressItem.bind(this);
  }

  async onFileSelected() {
    let isPDFValid = await this.validatePDF();

    if (isPDFValid && this.validateUpload()) {
      this.uploader.queue.map((x) => {
        this.sendFile(x);
      });
    }
    else {
      this.uploader.queue = [];
    }
  }

  validatePDF() {
    const q = this.uploader.queue.map(async (x) => {
      // if (x._file.type !== 'application/pdf') {
      //   return true;
      // }
      return await this.checkFileProtection(x._file);
    });
    return Promise.all(q).then(() => {
      return true;
    }).catch(() => {
      return false;
    });
  }
  
  validateUpload() {
    if (this.uploader.queue.length > 1) {
      this.util.openSnackBar("Please drop one file at a time", 'Error');
      return false;
    }
    if (this.uploader.queue.some(i => !i.file.size)) {
      this.util.openSnackBar("The selected file does not have any content", 'Error');
      return false;
    }
    if (!this.validateDocType()) {
      this.util.openSnackBar("Document type not selected", 'Error');
      return false;
    }
    if (this.isReadonly()) {
      this.util.openSnackBar("File upload not allow", 'Error');
      return false;
    }
    if (this.appConfig.maxFileCount < (this.uploader.queue.length + this.uploadSummary.length)) {
      this.util.openSnackBar("Max file upload limit is " + this.appConfig.maxFileCount, 'Error');
      return false;
    }
    if (!this.validateFileNames()) {
      this.uploader.queue = [];
      this.util.openSnackBar("Filename should be less than or equal to " + this.appConfig.maxFileNameLength + ' characters', 'Error');
      return false;
    }
    if (!this.validateFileNameCharacters()) {
      this.uploader.queue = [];
      return false;
    }
    return true;
  }

  validateFileNames() {
    return this.uploader.queue.every(x => x.file.name.length <= 100);
  }

  validateFileNameCharacters() {
    const invalidChars = /[\\\/:*?"<>|]/g;
    for (const item of this.uploader.queue) {
      const matches = item.file.name.match(invalidChars);
      if (matches) {
        const unique = Array.from(new Set(matches)).join(' ');
        this.util.openSnackBar(
          `Filename "${item.file.name}" contains characters that are not allowed: ${unique}. Please rename the file and try again.`,
          'Error'
        );
        return false;
      }
    }
    return true;
  }

  setUploadConfig() {
    this.appConfig.maxFileCount = this.appConfig.maxFileCount || 5;
    this.appConfig.maxFileNameLength = this.appConfig.maxFileNameLength || 100;
    this.uploaderOptions['method'] = this.appConfig.method;
    this.uploaderOptions['autoUpload'] = this.appConfig.autoUpload;
    this.uploaderOptions['maxFileSize'] = this.appConfig.maxFileSize;
    this.uploaderOptions['allowedMimeType'] = this.appConfig.allowedMimeType;
    this.uploaderOptions['disableMultipart'] = true;
    this.uploader.setOptions(this.uploaderOptions);
    this.multiple = this.appConfig.multiple;
    this.size = this.appConfig.maxFileSize / (1024 * 1024);
    this.extension = this.appConfig.allowedType;
  }

  sendFile(x: any) {
    const queueIndex = this.uploader.getIndexOfItem(x);
    const queueItem = this.uploader.queue[queueIndex];
    this.findPreSignedURL(queueItem).subscribe({
      next: (response: any) => {
        let fileSummary: UploadSummary = response;
        fileSummary.status = "uploading";
        this.uploadSummary.push(fileSummary)
        queueItem.alias = fileSummary.id;
        queueItem.url = fileSummary.preSignedURL || "";
        queueItem.method = this.appConfig.method;
        queueItem.headers = [{ name: "Content-Type", value: queueItem.file.type }];
        this.spin.show();
        queueItem.upload();
        if (!this.isOneDocType) {
          this.initDocType();
        }
      },
      error: (error: any) => {
        console.log({ error });
      }
    });
  }

  findPreSignedURL(queueItem: FileItem) {
    const filename = queueItem.file.name;
    const filetype = queueItem.file.type;
    const filesize = queueItem.file.size;

    let displayFilename = this.selectedDocTypes.documentType.code ? this.selectedDocTypes.documentType.code + " - " : "";
    displayFilename += this.selectedDocTypes.documentSubType.code ? this.selectedDocTypes.documentSubType.code + " - " : "";
    displayFilename += queueItem.file.name;
    displayFilename = displayFilename.replace(/[^a-zA-Z0-9-_. ]/g, "");

    const payload = {
      "appId": this.artifact?.appId,
      "status": ARTIFACT_STATUS_LABEL[this.artifact?.artifactStatus],
      "artifactId": this.artifact?.artifactId,
      "name": displayFilename,
      "mimeType": filetype,
      "size": filesize,
      "documentType": this.selectedDocTypes.documentType.code,
      "documentSubType": this.selectedDocTypes.documentSubType.code
    };
    return this.http.put('file', payload);
  }

  remove(index: any, id: string) {
    this.uploader.queue.splice(index, 1);
    const payload = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      body: {
        "id": id,
        "appId": this.artifact?.appId
      }
    };
    this.http.delete('file', payload)
      .subscribe((x: any) => {
        this.http.delete(x.preSignedURL).subscribe();
        console.log(x);
        this.uploadSummary.splice(index, 1);
        this.checkValid();
        this.updateArtifact();
      });
  }

  removeuploaded(item: UploadSummary) {
    // this.uploadSummary.splice(item, 1);
    let index = this.uploadSummary.findIndex(i => i.id == item.id);
    const payload = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      body: {
        "id": item.id,
        "appId": this.artifact?.appId
      }
    };
    this.http.delete('file', payload)
      .subscribe((x: any) => {
        this.http.delete(x.preSignedURL).subscribe();
        console.log(x);
        this.uploadSummary.splice(index, 1);
        this.checkValid();
        this.updateArtifact();
      });
  }
  updateArtifact() {
    // BM 6-30-23 All file uploads should trigger save
    if (this.artifactUpload && this.artifact.type == ARTIFACT_TYPE.FileUploadArtifact) {
      // A failed upload leaves no file in S3, so the section must not be marked valid.
      this.artifact.sectionValid = !this.hasFailedUploads();
      this.appWizService.updateAppWizard(this.artifact).subscribe();
    }
  }

  hasFailedUploads(): boolean {
    return this.failedUploads.length > 0;
  }

  failedUploadNames(): string {
    return this.failedUploads.map(f => f.fileName).join(', ');
  }

  checkValid() {
    if (this.uploader.queue.length > 0) this.emitData.emit(true);
    else this.emitData.emit(false);
  }

  validateDocType() {
    if (this.docTypes?.length > 1 && !this.selectedDocTypes?.documentType?.code) {
      return false;
    }
    if (this.selectedDocTypes?.documentType?.subTypes?.length > 1 && !this.selectedDocTypes?.documentSubType?.code) {
      return false;
    }
    return true;
  }

  isReadonly() {
    return this.appWizService.isReadOnly(this.artifact);
  }

  viewDocument(item: any) {
    const artifact: Artifact = this.artifact;
    this.http.get('file', { id: item.id }).subscribe((x: any) => {
      if (x.preSignedURL) {
        this.util.urlToBlob(x.preSignedURL, {}, item.name).subscribe();
      }
    });
  }

  checkViewerRoles(item: any) {
    const user = this.userService.getUser();
    if (user.alsdeId == this.artifact.userId) {
      return true;
    }
    let selectedRole: any = this.userService.getSelectedSiteRole()?.RoleName;

    if (this.artifact.key == ARTIFACT_KEY.MOREINFO) {
      if (item.documentType == 'BGR' && selectedRole != USER_ROLES.APPLICANT) {
        //more-info-needed artifact BGR docs should only be visible by applicant only
        return false;
      }
    }

    if (this.artifact.viewerRoles?.length) {
      return this.artifact.viewerRoles.includes(selectedRole);
    }
    return true;
  }

  onWhenAddingFileFailed(item: any, filter: any, options: any) {
    console.log("onWhenAddingFileFailed", { item, filter, options })
    if (filter.name == 'fileSize') {
      this.util.openSnackBar(`File size exceeds the maximum of ${this.size} MB per file.You can upload mutiple files if required.`, "Error");
    }
    if (filter.name == 'mimeType') {
      this.util.openSnackBar(`The selected file type is not a valid file type. The following file formats only are supported *.${this.extension.join(", *.")}`, "Error");
    }
  }
  onErrorItem(item: FileItem, response: string, status: number, headers: ParsedResponseHeaders): any {
    console.log("onErrorItem", { item, response, status, headers });
    console.log(`S3 file upload is failed for ${item.url}: ${status}`);
    this.handleUploadFailure(item, status, response);

    // Report the failure to the backend so the orphan Document row gets marked
    // failed and the failure is captured in CloudWatch. item.alias holds the
    // document id assigned by sendFile().next when the presigned URL was issued.
    this.reportUploadFailure(item.alias, status, response);
  }

  onCancelItem(item: FileItem, response: string, status: number, headers: ParsedResponseHeaders): any {
    // ng2-file-upload routes aborted requests here, not to onErrorItem. An aborted
    // upload never reached S3 either, so it gets identical treatment.
    console.log("onCancelItem", { item, response, status, headers });
    this.handleUploadFailure(item, status, response);
    this.reportUploadFailure(item.alias, status, response);
  }

  /**
   * The single point at which an upload is known to have failed. Everything that has
   * to react hangs off here rather than being re-derived by each caller: the document
   * entry created before the S3 PUT is dropped, the user is told, and the failure is
   * bubbled to the host artifact so the section cannot be marked complete.
   */
  private handleUploadFailure(item: FileItem, status: number, response: string) {
    this.spin.hide();
    this.uploader.removeFromQueue(item);

    // Drop the entry pushed by sendFile() before the upload was attempted. Leaving it
    // behind is what recorded documents that never reached S3 (ALCS12-522).
    const index = this.uploadSummary.findIndex(i => i.id == item.alias);
    if (index > -1) {
      this.uploadSummary.splice(index, 1);
    }

    this.failedUploads.push({
      documentId: item.alias,
      fileName: item.file.name,
      status: status,
      response: response
    });

    this.util.openSnackBar(`${item.file.name} failed to upload. Please upload it again.`, 'Error');

    this.uploadFailure.emit(true);
    this.checkValid();
  }

  /** Clears the outstanding failure for a file once it has been uploaded successfully. */
  private clearUploadFailure(fileName: string) {
    if (!this.failedUploads.length) return;

    this.failedUploads = this.failedUploads.filter(f => f.fileName != fileName);
    if (!this.failedUploads.length) {
      this.uploadFailure.emit(false);
    }
  }

  /**
   * Best-effort POST to /file telling the backend a client-side upload failed.
   * Backend marks the Document row status=failed and logs the error.
   * We don't surface errors from this call — failure reporting must not itself
   * fail the user-visible flow.
   */
  reportUploadFailure(documentId: string | undefined, status: number, response: string) {
    if (!documentId) return;
    const payload = {
      id: documentId,
      appId: this.artifact?.appId,
      status: status,
      error: response
    };
    this.http.post('file', payload).subscribe({
      next: () => {},
      error: (err) => console.log('reportUploadFailure failed', err)
    });
  }

  onCompleteAll() {
    this.uploader.clearQueue();
  }

  onSuccessItem(fileItem: FileItem, response: string, status: number, headers: ParsedResponseHeaders): any {
    this.spin.hide();
    this.uploadSummary.map(i => {
      if (i.id == fileItem.alias) {
        i.status = "uploaded";
      }
    });

    this.uploader.removeFromQueue(fileItem);
    this.clearUploadFailure(fileItem.file.name);
    this.checkValid();
    // this.updateArtifact();
  }

  onProgressItem(fileItem: FileItem, progress: any): any {
    console.log({
      p1: fileItem.progress,
      p2: progress
    })
    this.uploadSummary.map(i => {
      if (i.id == fileItem.alias) {
        i.progress = progress;
      }
    });
  }

  getProgressValue(uploadSummaryItem: UploadSummary) {
    return uploadSummaryItem.status == 'uploaded' ? 100 : uploadSummaryItem.progress;
  }



  //password protected check
  async checkFileProtection(file: File): Promise<boolean> {
    const type = file.type;

    if (type === 'application/pdf') {
      return await this.checkPdfPassword(file);
    }

    if (
      type.includes('spreadsheet') ||
      type.includes('word') ||
      type.includes('officedocument')
    ) {
      return await this.isOfficeFileProtected(file);
    }

    if (type === 'application/zip') {
      return await this.isZipProtected(file);
    }

    return false; // Unknown type
  }

  checkPdfPassword(file: File): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const typedArray = new Uint8Array(reader.result as ArrayBuffer);
        try {
          await pdfjsLib.getDocument({ data: typedArray }).promise;
          resolve(true);
        }
        catch (error: any) {
          if (error?.name === 'PasswordException') {
            this.util.openSnackBar('The PDF is password-protected and cannot be uploaded.', 'Error');
            reject(false);
          } else {
            this.util.openSnackBar('Selected PDF cannot be uploaded.', 'Error');
            reject(false);
          }
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }

  async isZipProtected(file: File): Promise<boolean> {
    const arrayBuffer = await file.arrayBuffer();

    try {
      await JSZip.loadAsync(arrayBuffer);
      return true;
    } catch {
      this.util.openSnackBar('The file is password-protected and cannot be uploaded.', 'Error');
      throw new Error('Encrypted Zip file');
    }
  }

  async isOfficeFileProtected(file: File): Promise<boolean> {
    const arrayBuffer = await file.arrayBuffer();
    try {
      const zip = await JSZip.loadAsync(arrayBuffer);
      // Encrypted office files contain this file
      if (zip.file('EncryptedPackage')) {
        // return true;
        this.util.openSnackBar('The file is password-protected and cannot be uploaded.', 'Error');
        throw new Error('Encrypted Office file'); // To be caught in catch block
      }
      return true;
    } catch (err) {
      // return true; // Likely encrypted or corrupted
      this.util.openSnackBar('The file is password-protected and cannot be uploaded.', 'Error');
      throw new Error('Encrypted Office file');
    }
  }
}
